import {
  BookingSupplierListingVariant,
  CitedRegistration,
  EapVariantPrice,
  EventSupplierListingVariant,
  GetSupplierListingResponse,
  MarketplaceVariantPrice,
  OfferSupplierListingVariant,
  PassSupplierListingVariant,
  ProductSupplierListingVariant,
  ReviewSalePrice,
  SubscriptionSupplierListingVariant,
  SupplierListingVariant,
  UpdateDraftListingRequest,
  UpdateSupplierSkuQuantitiesRequest,
  VariantPrice,
} from "@givenwell/management-api";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { autoMessagesQuery } from "./mgmtMessages";
import { supplierListingsQueryKey } from "./supplier";
import { api } from "./utils";

export const pendingListingsQueryKey = ["pending-listings"];

export const pendingListingsQuery = (supplierId: string | undefined, page = 1, pageSize = 10) =>
  queryOptions({
    queryKey: [...pendingListingsQueryKey, { supplierId, page, pageSize }],
    queryFn: async () => {
      const response = await api.listings.getPendingListings({ page, pageSize, supplier: supplierId });
      return response;
    },
  });

export const listingsQueryKey = ["listings"];

export const listingQuery = (listingId: string, version: number) =>
  queryOptions({
    queryKey: [...listingsQueryKey, listingId, version],
    queryFn: async () => {
      const response = await api.listings.getSupplierListing({ listingId, version });
      return response;
    },
  });

export const TagSet = {
  Specialty: "speciality",
  SupportType: "aos",
  Gender: "gender",
  Language: "language",
} as const;
export type TagSet = (typeof TagSet)[keyof typeof TagSet];

export const tagSetQuery = (tagSet: TagSet) =>
  queryOptions({
    queryKey: ["tag-set", tagSet],
    queryFn: async () => await api.listings.getTagSet({ tagSet }),
  });

export const listingReviewQuery = (version: number, listingId: string) =>
  queryOptions({
    queryKey: ["listing-review", { version, listingId }],
    queryFn: async () => {
      const response = await api.listings.getListingReview({ listingId, version });
      return response;
    },
  });

export const registersQuery = (countryCode: string) =>
  queryOptions({
    queryKey: ["registers", countryCode],
    queryFn: async () => {
      const response = await api.listings.getRegisters({ country: countryCode });
      return response;
    },
  });

export const registerMembershipTypesQuery = (registerId: string) =>
  queryOptions({
    queryKey: ["register", registerId, "membership-types"],
    queryFn: async () => {
      const response = await api.listings.getRegisterMembershipTypes({ registerId });
      return response.membership_types;
    },
  });

// -------------------------------------------------------------------------------------------------

export function useReviewListingMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      listingId: string;
      version: number;
      action: "approve" | "reject";
      reason?: string;
      cited_registration?: CitedRegistration;
      salePrices?: ReviewSalePrice[];
      notifySupplier?: boolean;
    }) => {
      return await api.listings.reviewPendingListing({
        listingId: args.listingId,
        version: args.version,
        requestBody: {
          cited_registration: args.cited_registration,
          review_type: args.action,
          reason: args.reason ?? "No reason given.",
          sale_prices: args.salePrices,
          notify_supplier: args.notifySupplier ?? true,
        },
      });
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: pendingListingsQueryKey });
      client.removeQueries({ queryKey: listingsQueryKey });
    },
  });
}

export function useSaveDraftMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string; version: number; request: UpdateDraftListingRequest }) => {
      return await api.listings.updateDraftListing({
        listingId: args.listingId,
        version: args.version,
        requestBody: args.request,
      });
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: supplierListingsQueryKey(supplierId) });
      client.removeQueries({ queryKey: listingsQueryKey });
    },
  });
}

export function useStartDraftMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string }) => {
      return await api.listings.startDraftListingVersion({ listingId: args.listingId });
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: supplierListingsQueryKey(supplierId) });
      client.removeQueries({ queryKey: listingsQueryKey });
      client.removeQueries(autoMessagesQuery(supplierId));
    },
  });
}

export function useSubmitDraftForReviewMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string; version: number }) => {
      return await api.listings.submitListingVersion({ listingId: args.listingId, version: args.version });
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: supplierListingsQueryKey(supplierId) });
      client.removeQueries({ queryKey: listingsQueryKey });
    },
  });
}

export function useUndoDraftSubmissionMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string; version: number }) => {
      return await api.listings.cancelListingVersionSubmission({ listingId: args.listingId, version: args.version });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: listingsQueryKey });
      client.removeQueries({ queryKey: supplierListingsQueryKey(supplierId) });
    },
  });
}

export function useDeleteDraftMutation(supplierId: string | undefined) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string; version: number }) => {
      return await api.listings.deleteListingVersion({ listingId: args.listingId, version: args.version });
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: supplierListingsQueryKey(supplierId) });
      client.removeQueries({ queryKey: listingsQueryKey });
    },
  });
}

export function usePauseListingMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string }) => {
      return await api.listings.pauseListing({ listingId: args.listingId });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: supplierListingsQueryKey(supplierId) });
    },
  });
}

export function useResumeListingMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string }) => {
      return await api.listings.resumeListing({ listingId: args.listingId });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: supplierListingsQueryKey(supplierId) });
      client.invalidateQueries({ queryKey: listingsQueryKey });
    },
  });
}

export function useArchiveListingMutation(supplierId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { listingId: string }) => {
      return await api.listings.archiveListing({ listingId: args.listingId });
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: supplierListingsQueryKey(supplierId) });
      client.removeQueries({ queryKey: listingsQueryKey });
    },
  });
}

// Typescript utils

export type Listing = GetSupplierListingResponse;
export type ListingStatus = Listing["status"];
export type ListingDeliveryMethod = NonNullable<Listing["delivery_method"]>;

export type BaseListingVariant = SupplierListingVariant;
export type ListingVariant = Listing["variants"][number];
export type VariantLocationType = NonNullable<ListingVariant["location_type"]>;
export type VariantType = ListingVariant["type_id"];

export function isBookingVariant(variant: BaseListingVariant): variant is BookingSupplierListingVariant {
  return variant.$type === "booking";
}
export function isProductVariant(variant: BaseListingVariant): variant is ProductSupplierListingVariant {
  return variant.$type === "product";
}
export function isEventVariant(variant: BaseListingVariant): variant is EventSupplierListingVariant {
  return variant.$type === "event";
}
export function isSubscriptionVariant(variant: BaseListingVariant): variant is SubscriptionSupplierListingVariant {
  return variant.$type === "subscription";
}
export function isPassVariant(variant: BaseListingVariant): variant is PassSupplierListingVariant {
  return variant.$type === "pass";
}
export function isOfferVariant(variant: BaseListingVariant): variant is OfferSupplierListingVariant {
  return variant.$type === "offer";
}

export const supplierSkuQuery = (supplierId: string) =>
  queryOptions({
    queryKey: ["suppliers", supplierId, "skus"],
    queryFn: async () => {
      return await api.listings.getSupplierSkus({ supplierId });
    },
  });

export function useUpdateSKUQuantitiesMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { supplierId: string; changes: UpdateSupplierSkuQuantitiesRequest }) => {
      return await api.listings.updateSupplierSkuQuantities({ supplierId: args.supplierId, requestBody: args.changes });
    },
    onSettled: (_, __, variables) => {
      client.invalidateQueries(supplierSkuQuery(variables.supplierId));
    },
  });
}

export function isMarketplaceVariantPrice(price: VariantPrice): price is MarketplaceVariantPrice {
  return !price.$type || price.$type === "marketplace";
}

export function isEapVariantPrice(price: VariantPrice): price is EapVariantPrice {
  return price.$type === "eap";
}

export type EapPriceCurrencies = EapVariantPrice["currency"];
export const EAP_PRICE_CURRENCIES: EapPriceCurrencies[] = [
  // Needed for valibot.
  "aed",
  "ars",
  "aud",
  "bdt",
  "bhd",
  "bob",
  "brl",
  "cad",
  "chf",
  "clp",
  "cny",
  "cop",
  "czk",
  "dkk",
  "egp",
  "eur",
  "gbp",
  "hkd",
  "hrk",
  "huf",
  "idr",
  "ils",
  "inr",
  "jpy",
  "krw",
  "lkr",
  "mad",
  "mxn",
  "myr",
  "nok",
  "npr",
  "nzd",
  "pen",
  "php",
  "pkr",
  "pln",
  "ron",
  "sar",
  "sek",
  "sgd",
  "thb",
  "try",
  "usd",
  "uyu",
  "vnd",
  "zar",
] as const;
